/**
 * Welcome — post-consent vault interface for whitelisted wallets.
 *
 * Tabs:
 *   - Deposit: gated by walletCap (whitelisted + headroom).
 *   - Redeem:  gated by an existing position (shares held OR in-flight redemptions).
 *   - Claim:   gated by an existing position; settles finalized requests.
 *
 * Gating (page-level):
 *   - No wallet connected → bounce to /early-access (consent flow start).
 *   - Connected wallet doesn't match the URL slug → soft mismatch notice.
 *   - walletCapConfigured(connected) === false → deposit blocked, but
 *     Redeem/Claim still surface if the wallet already has a position
 *     (e.g. cap was revoked after a prior deposit).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ConnectButton, useWalletContext } from "@thema/ui/wallet";
import {
  preflightDeposit,
  usePreviewDeposit,
  useTokenApproval,
  useVaultAssetMetadata,
  useVaultConfig,
  useVaultData,
  useVaultShareDecimals,
  useVaultWrite,
  useWallet,
  useWalletCapStatus,
  waitForVaultReceipt,
} from "@thema/ui";
import { useTokenBalance } from "@thema/ui/wallet";
import { getChainConfig } from "@thema/sdk/ui";
import type { UserVaultPosition, VaultConfig, VaultState } from "@thema/ui";

import { CONSENT_DEPLOYMENT_ID, CONSENT_VAULT_REF } from "./consent-config";

// ThemaLPVault is itself the ERC-4626 share token — its share symbol comes
// from the ERC-20 metadata baked into the vault contract, not from the OMT
// registry (which describes the underlying asset, e.g. "cBTC"). For this MVP
// we hardcode the user-facing label here so it can be swapped per-vault
// without chasing the metadata through every component.
//
// Note: the share token *address* is the vault address itself (vault.vaultAddress).
const SHARE_SYMBOL = "ThemaVaultShare";

// On-chain reads polling cadence (vault state + cap + user position). 15s ≈
// 7-8 blocks on Base — fast enough for the position cells to reflect a fresh
// deposit, slow enough to not hammer public RPC endpoints.
const POLL_INTERVAL_MS = 15_000;

type Tab = "deposit" | "redeem" | "claim";

function isAddress(value: string | undefined): value is `0x${string}` {
  return !!value && /^0x[a-fA-F0-9]{40}$/.test(value);
}

function shortenAddress(addr: string) {
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

/**
 * Inline copy-to-clipboard control. Falls back to a no-op if clipboard API
 * is unavailable (e.g. insecure context) — we don't want to crash the row
 * just because some browser denied the permission.
 */
function CopyButton({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      console.error("[copy] failed:", e);
    }
  }, [value]);

  return (
    <button
      type="button"
      onClick={copy}
      title={label ? `Copy ${label}` : "Copy"}
      className="font-mono text-[10px] uppercase tracking-[0.15em] text-thema-micro hover:text-white/70 transition-colors"
    >
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function formatAmount(value: string, fractionDigits = 4) {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return n.toLocaleString(undefined, {
    maximumFractionDigits: fractionDigits,
  });
}

const SLUG_PREFIX = "welcome-";

export default function Welcome() {
  const params = useParams<{ welcomeSlug?: string }>();
  const slug = params.welcomeSlug;
  const addressPart =
    slug && slug.toLowerCase().startsWith(SLUG_PREFIX)
      ? slug.slice(SLUG_PREFIX.length)
      : undefined;
  const slugAddress = isAddress(addressPart)
    ? (addressPart.toLowerCase() as `0x${string}`)
    : undefined;

  const { address, isConnected, isConnecting, chainId, shortAddress } = useWallet();
  const vault = useVaultConfig(CONSENT_DEPLOYMENT_ID, CONSENT_VAULT_REF);
  if (!vault) {
    throw new Error(
      `Vault config missing for deployment "${CONSENT_DEPLOYMENT_ID}".`,
    );
  }

  const vaultChainIdNum = parseInt(vault.chainId, 10);
  const isOnCorrectChain = chainId === vaultChainIdNum;

  const connected = (address as `0x${string}` | undefined) ?? undefined;

  // Source the deposit asset & cap-accounting token straight from chain.
  // Off-chain registry metadata has historically mis-labeled these.
  const {
    metadata: assetMeta,
    isLoading: assetMetaLoading,
    error: assetMetaError,
  } = useVaultAssetMetadata({ vault });

  const assetSymbol = assetMeta?.asset.symbol ?? "";
  const assetDecimals = assetMeta?.asset.decimals ?? 0;
  const accountingSymbol = assetMeta?.accountingBase.symbol ?? "";
  const accountingDecimals = assetMeta?.accountingBase.decimals ?? 0;
  const capDecimals = assetMeta
    ? { assetDecimals, accountingDecimals }
    : undefined;

  const {
    status: capStatus,
    isLoading: capLoading,
    error: capError,
    refetch: refetchCap,
  } = useWalletCapStatus({ vault, wallet: connected, decimals: capDecimals });

  const {
    vaultState,
    userPosition,
    isLoading: vaultLoading,
    refetch: refetchVault,
  } = useVaultData({
    vault: assetMeta ? { ...vault, assetSymbol, assetDecimals } : vault,
    userAddress: connected,
  });

  // Real share decimals from the chain (vault.decimals()). For ThemaLPVault
  // this is assetDecimals + 6 (decimalsOffset), but we read it directly so the
  // UI doesn't silently inflate redeem amounts if the offset ever changes.
  const shareDecimalsFromChain = useVaultShareDecimals(vault);
  const shareDecimals = shareDecimalsFromChain ?? assetDecimals + 6;

  const writes = useVaultWrite({
    vaultAddress: vault.vaultAddress,
    chainId: vault.chainId,
    assetDecimals,
    shareDecimals,
  });

  const explorerBase = useMemo(() => {
    try {
      const { chain } = getChainConfig(vault.chainId);
      return chain.blockExplorers?.default?.url?.replace(/\/$/, "");
    } catch {
      return undefined;
    }
  }, [vault.chainId]);

  const isWhitelisted = capStatus?.configured === true;
  const remaining = capStatus?.maxDeposit ?? "0";
  const remainingWei = capStatus?.maxDepositWei ?? 0n;
  const cap = capStatus?.effectiveCap ?? "—";
  const exposure = capStatus?.exposure ?? "—";

  // Position value = (shareBalance / totalSupply) × totalAssets — asset worth
  // of the user's shares at the vault's current min(spot, TWAP) NAV. The
  // strings from useVaultData are already human-formatted decimals, so
  // Number() is fine for display.
  const totalSupplyNum = vaultState ? Number(vaultState.totalSupply) : NaN;
  const totalAssetsNum = vaultState ? Number(vaultState.totalAssets) : NaN;
  const shareBalanceNum = Number(userPosition?.shareBalance ?? "0");
  const positionValue =
    Number.isFinite(shareBalanceNum) &&
    Number.isFinite(totalSupplyNum) &&
    Number.isFinite(totalAssetsNum) &&
    totalSupplyNum > 0
      ? (shareBalanceNum / totalSupplyNum) * totalAssetsNum
      : null;

  // A wallet "has a position" if it holds shares OR has in-flight redemptions.
  // Redeem/Claim surface based on this — independent of current whitelist state,
  // so an off-boarded wallet can still exit.
  const positionShares = Number(userPosition?.shareBalance ?? "0");
  const positionPending = Number(userPosition?.pendingShares ?? "0");
  const positionClaimable = Number(userPosition?.claimableShares ?? "0");
  const hasPosition =
    positionShares > 0 ||
    positionPending > 0 ||
    positionClaimable > 0 ||
    (userPosition?.requestIds.length ?? 0) > 0;

  const refetchAll = useCallback(async () => {
    await Promise.all([refetchVault(), refetchCap()]);
  }, [refetchVault, refetchCap]);

  // Keep on-chain reads live without forcing the user to reload — drives the
  // position cells so they reflect a fresh deposit/redeem within one poll.
  useEffect(() => {
    if (!isConnected) return;
    const id = setInterval(() => {
      void refetchAll();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isConnected, refetchAll]);

  const { disconnect, openModal } = useWalletContext();

  // Enforce slug presence — invalid slug bounces home.
  if (!slugAddress) return <Navigate to="/early-access" replace />;

  // Wallet adapter rehydrates async on reload.
  if (isConnecting) {
    return (
      <main className="min-h-screen bg-[#0A0F16] text-thema-text px-6 py-12 md:py-20">
        <article className="mx-auto max-w-[560px] text-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-thema-micro">
            Reconnecting wallet…
          </p>
        </article>
      </main>
    );
  }

  // ─── Gate 1: Wallet not connected ───────────────────────────────────────────
  if (!isConnected) {
    return (
      <main className="min-h-screen bg-[#0A0F16] text-thema-text px-6 py-12 md:py-20">
        <article className="mx-auto max-w-[560px] space-y-8 text-center">
          <header className="space-y-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-thema-teal/80">
              EARLY ACCESS
            </p>
            <h1 className="font-serif text-2xl md:text-3xl font-semibold leading-tight">
              Welcome to Thema Beta Early Access
            </h1>
            <p className="text-[14px] leading-relaxed text-thema-text-soft/80">
              Please connect your wallet to continue.
            </p>
          </header>
          <div className="flex justify-center">
            <ConnectButton variant="outline" size="lg" />
          </div>
          <div className="pt-4">
            <Link
              to="/early-access/consent"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-thema-border-hairline bg-white/[0.04] font-mono text-[11px] uppercase tracking-[0.15em] text-white/70 hover:bg-white/[0.08] hover:text-white transition-colors"
            >
              ← Back to consent
            </Link>
          </div>
        </article>
      </main>
    );
  }

  // ─── Gate 2: Connected wallet doesn't match the URL ─────────────────────────
  if (connected && connected.toLowerCase() !== slugAddress) {
    return (
      <main className="min-h-screen bg-[#0A0F16] text-thema-text px-6 py-12 md:py-20">
        <article className="mx-auto max-w-[560px] space-y-8 text-center">
          <header className="space-y-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-thema-teal/80">
              EARLY ACCESS
            </p>
            <h1 className="font-serif text-2xl md:text-3xl font-semibold leading-tight">
              Welcome to Thema Beta Early Access
            </h1>
          </header>
          <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-5 space-y-3 text-left">
            <p className="text-[13px] text-amber-200/90 leading-relaxed">
              This link is for wallet:
            </p>
            <p className="font-mono text-[12px] text-white/90 break-all">
              {slugAddress}
            </p>
            <p className="text-[13px] text-amber-200/90 leading-relaxed">
              Not the same as your connected wallet. Please reconnect with the
              correct address.
            </p>
          </div>
          <button
            type="button"
            onClick={async () => {
              await disconnect();
              openModal();
            }}
            className="px-6 py-3 rounded-md border border-thema-royal-blue/60 bg-thema-royal-blue/15 text-white text-sm font-medium transition hover:bg-thema-royal-blue/25"
          >
            Reconnect wallet
          </button>
          <div className="pt-2">
            <Link
              to="/early-access/consent"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-thema-border-hairline bg-white/[0.04] font-mono text-[11px] uppercase tracking-[0.15em] text-white/70 hover:bg-white/[0.08] hover:text-white transition-colors"
            >
              ← Back to consent
            </Link>
          </div>
        </article>
      </main>
    );
  }

  // ─── Gate 3: Cap check still loading ────────────────────────────────────────
  if (!capStatus && !capError) {
    return (
      <main className="min-h-screen bg-[#0A0F16] text-thema-text px-6 py-12 md:py-20">
        <article className="mx-auto max-w-[560px] text-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-thema-micro">
            Checking your access…
          </p>
        </article>
      </main>
    );
  }

  // ─── Gate 4: Not whitelisted ────────────────────────────────────────────────
  if (capStatus && !isWhitelisted && !hasPosition) {
    return (
      <main className="min-h-screen bg-[#0A0F16] text-thema-text px-6 py-12 md:py-20">
        <article className="mx-auto max-w-[560px] space-y-8 text-center">
          <header className="space-y-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-thema-teal/80">
              EARLY ACCESS
            </p>
            <h1 className="font-serif text-2xl md:text-3xl font-semibold leading-tight">
              Welcome to Thema Beta Early Access
            </h1>
          </header>
          <div className="rounded-md border border-thema-border-hairline bg-white/[0.015] p-5 space-y-3 text-left">
            <p className="text-[13px] text-thema-text-soft/80 leading-relaxed">
              Your wallet is connected:
            </p>
            <p className="font-mono text-[12px] text-white/90 break-all">
              {connected}
            </p>
            <p className="text-[13px] text-thema-text-soft/80 leading-relaxed">
              This wallet is not yet whitelisted from Thema's side. You can reach
              us at{" "}
              <a
                href="mailto:early-access@thema.financial"
                className="text-thema-royal-blue hover:underline"
              >
                early-access@thema.financial
              </a>
            </p>
          </div>
          <div className="flex items-center justify-center gap-6 pt-2">
            <Link
              to="/early-access/consent"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-thema-border-hairline bg-white/[0.04] font-mono text-[11px] uppercase tracking-[0.15em] text-white/70 hover:bg-white/[0.08] hover:text-white transition-colors"
            >
              ← Back to consent
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-thema-royal-blue/50 bg-thema-royal-blue/10 font-mono text-[11px] uppercase tracking-[0.15em] text-thema-royal-blue hover:bg-thema-royal-blue/20 hover:text-white transition-colors"
            >
              Dashboard →
            </Link>
          </div>
        </article>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#0A0F16] text-thema-text px-6 py-12 md:py-20">
      <article className="mx-auto max-w-[560px] space-y-8">
        <header className="space-y-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-thema-teal/80">
            WHITELISTED · VAULT
          </p>
          <h1 className="font-serif text-2xl md:text-3xl font-semibold leading-tight">
            Welcome
          </h1>
          <p className="text-[14px] leading-relaxed text-thema-text-soft/80">
            Your wallet is approved for the <strong>Thema Bitcoin</strong> vault on{" "}
            {vault.chainName}. Deposit converts <strong>CBBTC</strong> to LP shares; redeem
            queues shares for the next settlement epoch.
          </p>
        </header>

        {/* Connected wallet & cap */}
        <section className="rounded-md border border-thema-border-hairline bg-white/[0.015] p-5 space-y-4">
          <div className="flex items-center justify-between gap-3 min-h-[40px]">
            <div className="flex items-center gap-3 min-w-0">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-thema-micro shrink-0">
                Wallet
              </span>
              <span className="text-[13px] truncate text-thema-text-soft">
                {shortAddress}
              </span>
            </div>
            <ConnectButton variant="outline" size="sm" />
          </div>

          <div className="flex items-center justify-between gap-3 min-w-0">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-thema-micro shrink-0">
              Vault
            </span>
            <div className="flex items-center gap-3 min-w-0">
              {explorerBase ? (
                <a
                  href={`${explorerBase}/address/${vault.vaultAddress}`}
                  target="_blank"
                  rel="noreferrer"
                  className="font-mono text-[12px] truncate text-thema-royal-blue hover:underline"
                  title={vault.vaultAddress}
                >
                  {shortenAddress(vault.vaultAddress)}
                </a>
              ) : (
                <span
                  className="font-mono text-[12px] truncate text-thema-text-soft"
                  title={vault.vaultAddress}
                >
                  {shortenAddress(vault.vaultAddress)}
                </span>
              )}
              <CopyButton value={vault.vaultAddress} label="vault address" />
            </div>
          </div>

          {assetMeta && (
            <div className="flex items-center justify-between gap-3 min-w-0">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-thema-micro shrink-0">
                {assetMeta.asset.symbol || "Token"}
              </span>
              <div className="flex items-center gap-3 min-w-0">
                {explorerBase ? (
                  <a
                    href={`${explorerBase}/address/${assetMeta.asset.address}`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-mono text-[12px] truncate text-thema-royal-blue hover:underline"
                    title={assetMeta.asset.address}
                  >
                    {shortenAddress(assetMeta.asset.address)}
                  </a>
                ) : (
                  <span
                    className="font-mono text-[12px] truncate text-thema-text-soft"
                    title={assetMeta.asset.address}
                  >
                    {shortenAddress(assetMeta.asset.address)}
                  </span>
                )}
                <CopyButton
                  value={assetMeta.asset.address}
                  label={`${assetMeta.asset.symbol || "token"} address`}
                />
              </div>
            </div>
          )}

          {!isConnected && (
            <div className="rounded-sm border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-[12px] text-amber-200/90 leading-relaxed">
              Reconnect your wallet to interact with the vault.
            </div>
          )}

          {!isOnCorrectChain && (
            <div className="rounded-sm border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-[12px] text-amber-200/90">
              Wrong network. Switch to {vault.chainName} to transact.
            </div>
          )}

          {assetMetaLoading && !assetMeta && (
            <p className="text-[12px] text-thema-micro">Reading vault asset…</p>
          )}

          {assetMetaError && (
            <p className="text-[12px] text-red-300/90 break-words">
              Could not read vault asset: {assetMetaError.message}
            </p>
          )}

          {(capLoading || (!capStatus && !capError)) && (
            <p className="text-[12px] text-thema-micro">Checking your access…</p>
          )}

          {capError && (
            <p className="text-[12px] text-red-300/90 break-words">
              Could not read wallet cap: {capError.message}
            </p>
          )}

          {isWhitelisted && (
            <div className="grid grid-cols-2 gap-3 pt-1">
              <div className="rounded-sm border border-thema-border-hairline bg-white/[0.015] p-3">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-thema-micro">
                  Approved limit (Worth)
                </p>
                <p className="mt-1 text-[14px] text-white/90 font-medium tabular-nums">
                  {cap === "Unlimited"
                    ? "Unlimited"
                    : `${formatAmount(cap, 2)} ${accountingSymbol}`}
                </p>
                <p className="mt-1 text-[10px] text-thema-text-soft/50">
                  Max lifetime exposure approved for this wallet.
                </p>
              </div>
              <div className="rounded-sm border border-thema-border-hairline bg-white/[0.015] p-3">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-thema-micro">
                  Already deposited (Worth)
                </p>
                <p className="mt-1 text-[14px] text-white/90 font-medium tabular-nums">
                  {formatAmount(exposure, 2)} {accountingSymbol}
                </p>
                <p className="mt-1 text-[10px] text-thema-text-soft/50">
                  Your current share of the vault, valued live.
                </p>
              </div>
              <div className="rounded-sm border border-thema-border-hairline bg-white/[0.015] p-3 col-span-2">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-thema-micro">
                  You can deposit now
                </p>
                <p className="mt-1 text-[16px] text-thema-teal/90 font-medium tabular-nums">
                  {formatAmount(remaining, 4)} {assetSymbol}
                </p>
                {remainingWei === 0n ? (
                  <p className="mt-1 text-[11px] text-amber-200/80">
                    Currently zero. This can happen if the vault is at its
                    global deposit cap, pricing data isn't ready, or you've
                    already deposited up to your approved limit. Try again
                    shortly or contact us if it persists.
                  </p>
                ) : (
                  <p className="mt-1 text-[11px] text-thema-text-soft/60">
                    The most you can deposit in this transaction. Always the
                    smaller of your remaining approved limit and the vault's
                    global remaining capacity. Refreshes after each deposit.
                  </p>
                )}
              </div>
              <div className="rounded-sm border border-thema-border-hairline bg-white/[0.015] p-3">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-thema-micro">
                  Total Vault Share Issued
                </p>
                <p className="mt-1 text-[14px] text-white/90 font-medium tabular-nums">
                  {vaultState
                    ? `${formatAmount(vaultState.totalSupply, 6)} ${SHARE_SYMBOL}`
                    : "…"}
                </p>
                <p className="mt-1 text-[10px] text-thema-text-soft/50">
                  Total shares issued by the vault.
                </p>
              </div>
              <div className="rounded-sm border border-thema-border-hairline bg-white/[0.015] p-3">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-thema-micro">
                  Total Vault Value
                </p>
                <p className="mt-1 text-[14px] text-white/90 font-medium tabular-nums">
                  {vaultState
                    ? `${formatAmount(vaultState.totalAssets, 8)} ${assetSymbol}`
                    : "…"}
                </p>
                <p className="mt-1 text-[10px] text-thema-text-soft/50">
                  Total asset value held by the vault at current NAV.
                </p>
              </div>
            </div>
          )}
        </section>

        {/* Position summary — only when the user actually has one */}
        {hasPosition && userPosition && (
          <PositionStrip
            position={userPosition}
            shareSymbol={SHARE_SYMBOL}
            assetSymbol={assetSymbol}
            positionValue={positionValue}
          />
        )}

        {/* Tabs — render once metadata is in hand */}
        {assetMeta && (
          <VaultTabs
            vault={vault}
            assetSymbol={assetSymbol}
            shareSymbol={SHARE_SYMBOL}
            assetDecimals={assetDecimals}
            shareDecimals={shareDecimals}
            assetAddress={assetMeta.asset.address}
            isOnCorrectChain={isOnCorrectChain}
            isWhitelisted={isWhitelisted}
            capCheckPending={!capStatus && !capError}
            hasPosition={hasPosition}
            remaining={remaining}
            remainingWei={remainingWei}
            connected={connected}
            vaultState={vaultState}
            vaultLoading={vaultLoading}
            userPosition={userPosition}
            writes={writes}
            refetchAll={refetchAll}
            explorerBase={explorerBase}
          />
        )}

        <div className="flex items-center justify-between pt-2">
          <Link
            to="/"
            className="font-mono text-[11px] uppercase tracking-[0.15em] text-thema-micro hover:text-white/70"
          >
            ← Back to thema.financial
          </Link>
        </div>
      </article>
    </main>
  );
}

// ─── Position summary ────────────────────────────────────────────────────────

function PositionStrip({
  position,
  shareSymbol,
  assetSymbol,
  positionValue,
}: {
  position: UserVaultPosition;
  shareSymbol: string;
  assetSymbol: string;
  positionValue: number | null;
}) {
  return (
    <section className="rounded-md border border-thema-border-hairline bg-white/[0.015] p-5 space-y-3">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-thema-micro">
        Your position
      </p>
      <div className="grid grid-cols-4 gap-3">
        <PositionCell
          label="Held"
          value={formatAmount(position.shareBalance, 6)}
          unit={shareSymbol}
        />
        <PositionCell
          label="Requested"
          value={formatAmount(position.pendingShares, 6)}
          unit={shareSymbol}
          accent="text-amber-300/90"
        />
        <PositionCell
          label="Claim"
          // 8 fraction digits so small cbBTC-style claims (sub-0.0001) don't
          // round to 0 in the cell; for 6-decimal assets the extra zeros are
          // harmless trailing precision.
          value={formatAmount(position.claimableAssets, 8)}
          unit={assetSymbol}
          accent="text-thema-teal/90"
        />
        <PositionCell
          label="Position value"
          value={
            positionValue === null
              ? "…"
              : formatAmount(String(positionValue), 8)
          }
          unit={positionValue === null ? undefined : assetSymbol}
          accent="text-thema-teal/90"
        />
      </div>
      {position.requestIds.length > 0 && (
        <p className="text-[11px] text-thema-text-soft/60">
          Request IDs:{" "}
          <span className="font-mono text-white/70">
            {position.requestIds.join(", ")}
          </span>
        </p>
      )}
    </section>
  );
}

function PositionCell({
  label,
  value,
  unit,
  accent = "text-white/90",
}: {
  label: string;
  value: string;
  unit?: string;
  accent?: string;
}) {
  return (
    <div className="rounded-sm border border-thema-border-hairline bg-white/[0.015] p-3">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-thema-micro min-h-[24px]">
        {label}
      </p>
      <p className={`mt-1 text-[15px] font-medium tabular-nums leading-tight ${accent}`}>
        {value}
      </p>
      {unit && (
        <p className="mt-0.5 text-[9px] font-mono tracking-[0.08em] text-thema-text-soft/50 break-words leading-tight">
          {unit}
        </p>
      )}
    </div>
  );
}

// ─── Tabs ────────────────────────────────────────────────────────────────────

interface TabsProps {
  vault: VaultConfig;
  assetSymbol: string;
  shareSymbol: string;
  assetDecimals: number;
  /** Live decimals from vault.decimals() (assetDecimals + 6 for ThemaLPVault). */
  shareDecimals: number;
  assetAddress: `0x${string}`;
  isOnCorrectChain: boolean;
  isWhitelisted: boolean;
  capCheckPending: boolean;
  hasPosition: boolean;
  remaining: string;
  remainingWei: bigint;
  connected: `0x${string}` | undefined;
  vaultState: VaultState | null;
  vaultLoading: boolean;
  userPosition: UserVaultPosition | null;
  writes: ReturnType<typeof useVaultWrite>;
  refetchAll: () => Promise<void>;
  explorerBase: string | undefined;
}

function VaultTabs(props: TabsProps) {
  const {
    isOnCorrectChain,
    isWhitelisted,
    capCheckPending,
    hasPosition,
    writes,
    explorerBase,
  } = props;

  const writeTxUrl =
    writes.hash && explorerBase ? `${explorerBase}/tx/${writes.hash}` : undefined;

  // Default to whichever tab the user is most likely to use.
  const initialTab: Tab = isWhitelisted
    ? "deposit"
    : hasPosition
    ? "redeem"
    : "deposit";
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);

  const tabs: { id: Tab; label: string; disabled: boolean; hint?: string }[] = [
    {
      id: "deposit",
      label: capCheckPending ? "Deposit…" : "Deposit",
      disabled: capCheckPending || !isWhitelisted,
      hint: capCheckPending ? "Checking access…" : (!isWhitelisted ? "Not whitelisted" : undefined),
    },
    {
      id: "redeem",
      label: "Request redeem",
      disabled: false,
    },
    {
      id: "claim",
      label: "Claim",
      disabled: false,
    },
  ];

  // If the chosen tab gets disabled (e.g. cap revoked between fetches), fall
  // back to the first enabled tab.
  const current = tabs.find((t) => t.id === activeTab);
  const effectiveTab: Tab =
    current && !current.disabled
      ? activeTab
      : (tabs.find((t) => !t.disabled)?.id ?? "deposit");

  return (
    <section className="rounded-md border border-thema-border-hairline bg-white/[0.015] overflow-hidden">
      <div className="grid grid-cols-3 border-b border-thema-border-hairline">
        {tabs.map((tab) => {
          const isActive = tab.id === effectiveTab;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => !tab.disabled && setActiveTab(tab.id)}
              disabled={tab.disabled}
              title={tab.hint}
              className={`px-3 py-3 text-[11px] font-mono uppercase tracking-[0.15em] transition-colors border-b-2 ${
                isActive
                  ? "border-thema-teal text-white"
                  : tab.disabled
                  ? "border-transparent text-white/20 cursor-not-allowed"
                  : "border-transparent text-thema-micro hover:text-white/70"
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="p-5">
        {effectiveTab === "deposit" && <DepositPanel {...props} />}
        {effectiveTab === "redeem" && <RedeemPanel {...props} />}
        {effectiveTab === "claim" && <ClaimPanel {...props} />}

        {/* Deposit panel manages its own tx status (waits for the receipt
            before claiming success). For redeem/claim we still surface the
            submitted hash + any wallet-rejection error. */}
        {effectiveTab !== "deposit" && writes.error && (
          <p className="mt-3 text-[12px] text-red-300/90 break-words">
            {writes.error.message}
          </p>
        )}

        {effectiveTab !== "deposit" && writes.hash && (
          <p className="mt-3 text-[11px] font-mono text-thema-micro break-all">
            tx:{" "}
            {writeTxUrl ? (
              <a
                href={writeTxUrl}
                target="_blank"
                rel="noreferrer"
                className="text-thema-royal-blue hover:underline"
              >
                {writes.hash}
              </a>
            ) : (
              writes.hash
            )}
          </p>
        )}

        {!isOnCorrectChain && (
          <p className="mt-3 text-[11px] text-amber-200/80">
            Switch to {props.vault.chainName} before submitting.
          </p>
        )}
      </div>
    </section>
  );
}

// ─── Deposit panel ───────────────────────────────────────────────────────────

type DepositPhase =
  | "idle"
  | "approving"
  | "depositing"
  | "confirmed"
  | "failed";

function Spinner() {
  return (
    <svg
      className="animate-spin h-4 w-4 inline-block align-text-bottom mr-2"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
      />
      <path
        className="opacity-90"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z"
      />
    </svg>
  );
}

function DepositPanel({
  vault,
  assetSymbol,
  assetDecimals,
  shareDecimals,
  assetAddress,
  isOnCorrectChain,
  isWhitelisted,
  remaining,
  remainingWei,
  connected,
  vaultState,
  vaultLoading,
  writes,
  refetchAll,
  explorerBase,
}: TabsProps) {
  const [amount, setAmount] = useState("");
  // Single state object: every phase transition is one setState → one render.
  // Splitting these into three useStates landed in React 19's scheduler at the
  // wrong moment (interleaving with wagmi's own state churn during a deposit)
  // and produced the "Should not already be working." invariant.
  const [tx, setTx] = useState<{
    phase: DepositPhase;
    hash: `0x${string}` | null;
    error: string | null;
  }>({ phase: "idle", hash: null, error: null });
  const phase = tx.phase;
  const confirmedHash = tx.hash;
  const errorMessage = tx.error;

  // Debounced share preview — calls vault.previewDeposit(amount) so the user
  // sees what they'd actually receive at submit time (uses deposit-context TVL,
  // i.e. max of spot/TWAP, so the preview can never lie favorably). Paused
  // while a tx is in flight to avoid re-rendering churn.
  const {
    shares: previewShares,
    isLoading: previewLoading,
  } = usePreviewDeposit({
    vault,
    amount,
    assetDecimals,
    shareDecimals,
    paused: phase === "approving" || phase === "depositing",
  });

  // We wait for the receipt ourselves — writeContract only resolves when the
  // tx is submitted (hash returned), not when it's mined. Without this wait,
  // a reverted deposit would still flash a "submitted" hash with no indication
  // that the on-chain execution failed. We poll via vault.rpcUrl rather than
  // the adapter's wagmi-bound waiter so a wallet that's on the wrong chain (or
  // whose chain state flickers mid-flight) doesn't leave us hanging.

  const {
    isApproved,
    approve,
    refetch: refetchApproval,
  } = useTokenApproval({
    token: assetAddress,
    spender: vault.vaultAddress,
    chainId: parseInt(vault.chainId, 10),
    requiredAmount: amount || "0",
    decimals: assetDecimals,
  });

  const {
    formatted: walletBalanceFormatted,
    balance: walletBalanceWei,
    isLoading: balanceLoading,
    refetch: refetchBalance,
  } = useTokenBalance({
    token: assetAddress,
    chainId: parseInt(vault.chainId, 10),
    account: connected,
  });

  // Mirror the page-level polling so balance/approval stay in sync with
  // out-of-band approvals or transfers without a manual reload.
  useEffect(() => {
    if (!connected) return;
    const id = setInterval(() => {
      void refetchBalance();
      void refetchApproval();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [connected, refetchBalance, refetchApproval]);

  const amountNum = Number(amount || "0");
  const remainingNum = Number(remaining);
  const balanceNum = Number(walletBalanceFormatted ?? "0");
  const exceedsCap =
    Number.isFinite(amountNum) &&
    Number.isFinite(remainingNum) &&
    amountNum > 0 &&
    amountNum > remainingNum;
  const exceedsBalance =
    Number.isFinite(amountNum) &&
    Number.isFinite(balanceNum) &&
    amountNum > 0 &&
    walletBalanceFormatted !== null &&
    amountNum > balanceNum;

  const clearTxStatus = useCallback(() => {
    setTx({ phase: "idle", hash: null, error: null });
  }, []);

  // viem wraps revert reasons in a few different fields depending on the call
  // path — collapse them here so we surface the most-specific message we can,
  // and log the raw error to the console for debugging.
  function describeWalletError(label: string, e: unknown): string {
    console.error(`[${label}] failed:`, e);
    const err = e as {
      shortMessage?: string;
      details?: string;
      metaMessages?: string[];
      cause?: { shortMessage?: string; message?: string };
      message?: string;
      name?: string;
    };
    if (err?.name === "UserRejectedRequestError") {
      return "You rejected the transaction in your wallet.";
    }
    return (
      err?.shortMessage ||
      err?.cause?.shortMessage ||
      (err?.metaMessages && err.metaMessages[0]) ||
      err?.cause?.message ||
      err?.details ||
      err?.message ||
      `${label} failed`
    );
  }

  const handleApprove = useCallback(async () => {
    if (!amount) return;
    setTx({ phase: "approving", hash: null, error: null });
    try {
      const { hash } = await approve();
      // Empty hash means the approval was a no-op (already allowance covers it).
      if (hash) {
        const receipt = await waitForVaultReceipt(
          vault,
          hash as `0x${string}`,
        );
        if (receipt.status !== "success") {
          throw new Error("Approval reverted on chain");
        }
      }
      await refetchApproval();
      setTx({ phase: "idle", hash: null, error: null });
    } catch (e) {
      setTx({
        phase: "failed",
        hash: null,
        error: describeWalletError("approve", e),
      });
    }
  }, [amount, approve, refetchApproval, vault]);

  const handleDeposit = useCallback(async () => {
    if (!connected || !amount) return;
    // Clear any prior error/hash before the new attempt; if the user is
    // retrying after a rejection, we don't want the old red box hanging around.
    setTx({ phase: "depositing", hash: null, error: null });
    try {
      // Pre-flight: mirrors the CLI deposit flow (maxDeposit check +
      // simulateContract). This catches reverts BEFORE MetaMask shows the
      // signature prompt, so the user sees an actionable reason instead of
      // a vague "MetaMask blocked the tx".
      const pre = await preflightDeposit({
        vault,
        account: connected,
        amount,
        assetDecimals,
        assetSymbol,
        receiver: connected,
      });
      if (!pre.ok) {
        if (pre.error) console.error("[deposit] preflight failed:", pre.error);
        throw new Error(pre.reason ?? "Deposit would revert on chain");
      }

      const { hash } = await writes.deposit(amount, connected);
      const receipt = await waitForVaultReceipt(
        vault,
        hash as `0x${string}`,
      );
      if (receipt.status !== "success") {
        throw new Error("Deposit reverted on chain — see explorer for details");
      }
      setTx({ phase: "confirmed", hash: hash as `0x${string}`, error: null });
      setAmount("");
      await Promise.all([
        refetchApproval(),
        refetchAll(),
        refetchBalance(),
      ]);
    } catch (e) {
      setTx({
        phase: "failed",
        hash: null,
        error: describeWalletError("deposit", e),
      });
    }
  }, [
    vault,
    connected,
    amount,
    assetDecimals,
    assetSymbol,
    writes,
    refetchApproval,
    refetchAll,
    refetchBalance,
  ]);

  const setMax = useCallback(() => {
    if (remainingWei === 0n) return;
    if (walletBalanceWei !== null && walletBalanceWei < remainingWei) {
      setAmount(walletBalanceFormatted ?? "0");
    } else {
      setAmount(remaining);
    }
  }, [remaining, remainingWei, walletBalanceWei, walletBalanceFormatted]);

  const isWorking = phase === "approving" || phase === "depositing";
  const needsApproval = !!amount && !isApproved;

  const canSubmit =
    isOnCorrectChain &&
    isWhitelisted &&
    !!amount &&
    !exceedsCap &&
    !exceedsBalance &&
    !isWorking;

  const confirmedTxUrl =
    confirmedHash && explorerBase
      ? `${explorerBase}/tx/${confirmedHash}`
      : undefined;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-thema-micro">
          Deposit
        </span>
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label className="text-[11px] text-thema-micro font-mono uppercase tracking-[0.15em]">
            Amount
          </label>
          <span className="text-[11px] text-thema-text-soft/70 font-mono">
            Balance:{" "}
            {balanceLoading && walletBalanceFormatted === null
              ? "…"
              : `${formatAmount(walletBalanceFormatted ?? "0", 6)} ${assetSymbol}`}
          </span>
        </div>
        <div className="relative flex items-center">
          <input
            type="number"
            inputMode="decimal"
            min={0}
            placeholder="0.00"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              if (phase === "confirmed" || phase === "failed") {
                clearTxStatus();
              }
            }}
            disabled={isWorking}
            className="w-full bg-white/[0.04] border border-thema-border-hairline rounded-md px-4 py-3 text-sm text-white/90 placeholder:text-white/25 outline-none focus:border-thema-royal-blue/60 transition-colors pr-24 disabled:opacity-60 disabled:cursor-not-allowed"
          />
          <div className="absolute right-2 flex items-center gap-2">
            <button
              type="button"
              onClick={setMax}
              disabled={remainingWei === 0n || isWorking}
              className="font-mono text-[10px] uppercase tracking-[0.15em] text-thema-royal-blue hover:underline disabled:opacity-40 disabled:hover:no-underline"
            >
              Max
            </button>
            <span className="text-[11px] text-white/40 font-mono">
              {assetSymbol}
            </span>
          </div>
        </div>

        {exceedsCap && (
          <p className="text-[11px] text-red-300/90">
            That's more than you can deposit right now (max{" "}
            {formatAmount(remaining, 4)} {assetSymbol}).
          </p>
        )}

        {!exceedsCap && exceedsBalance && (
          <p className="text-[11px] text-red-300/90">
            Amount exceeds your wallet balance (
            {formatAmount(walletBalanceFormatted ?? "0", 6)} {assetSymbol}).
          </p>
        )}

        {vaultState && (
          <p className="text-[11px] text-thema-text-soft/60">
            Share price ≈ {formatAmount(vaultState.sharePrice, 6)} {assetSymbol}
            {" "}per share
            {vaultLoading ? " · refreshing…" : ""}
          </p>
        )}

        {!!amount && !exceedsCap && !exceedsBalance && (
          <div className="rounded-sm border border-thema-border-hairline bg-white/[0.015] px-3 py-2 mt-1">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-thema-micro">
              You'll receive
              {isWorking && (
                <span className="ml-2 normal-case tracking-normal text-thema-text-soft/50">
                  · locked for this tx
                </span>
              )}
            </p>
            <p className="mt-1 text-[13px] font-medium tabular-nums text-thema-teal/90">
              {previewShares === null
                ? previewLoading
                  ? "Calculating…"
                  : "—"
                : `${formatAmount(previewShares, 6)} Vault Shares`}
              {previewShares !== null && previewLoading && !isWorking && (
                <span className="ml-2 text-[10px] text-thema-text-soft/60">
                  refreshing…
                </span>
              )}
            </p>
            <p className="mt-0.5 text-[10px] text-thema-text-soft/50">
              Estimated from on-chain previewDeposit · refreshes every 10s.
            </p>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={needsApproval ? handleApprove : handleDeposit}
        disabled={!canSubmit}
        className="w-full px-4 py-3 rounded-md border border-thema-royal-blue/60 bg-thema-royal-blue/15 text-white text-sm font-medium transition hover:bg-thema-royal-blue/25 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-thema-royal-blue/15"
      >
        {phase === "approving" ? (
          <>
            <Spinner />
            Approving {assetSymbol}…
          </>
        ) : phase === "depositing" ? (
          <>
            <Spinner />
            Depositing…
          </>
        ) : needsApproval ? (
          `Approve ${assetSymbol}`
        ) : (
          "Deposit"
        )}
      </button>

      {phase === "confirmed" && confirmedHash && (
        <div className="rounded-sm border border-emerald-500/40 bg-emerald-500/5 px-3 py-2 text-[12px] text-emerald-200/90 space-y-1">
          <p className="font-medium">Deposit confirmed.</p>
          <p className="font-mono text-[11px] break-all text-emerald-200/80">
            tx:{" "}
            {confirmedTxUrl ? (
              <a
                href={confirmedTxUrl}
                target="_blank"
                rel="noreferrer"
                className="text-thema-royal-blue hover:underline"
              >
                {confirmedHash}
              </a>
            ) : (
              confirmedHash
            )}
          </p>
        </div>
      )}

      {phase === "failed" && errorMessage && (
        <div className="rounded-sm border border-red-500/40 bg-red-500/5 px-3 py-2 text-[12px] text-red-200/90 break-words">
          {errorMessage}
        </div>
      )}
    </div>
  );
}

// ─── Redeem panel ────────────────────────────────────────────────────────────

function RedeemPanel({
  vault,
  assetSymbol,
  shareSymbol,
  isOnCorrectChain,
  connected,
  vaultState,
  userPosition,
  writes,
  refetchAll,
}: TabsProps) {
  const [shares, setShares] = useState("");

  const heldNum = Number(userPosition?.shareBalance ?? "0");
  const sharesNum = Number(shares || "0");
  const exceedsHeld =
    Number.isFinite(sharesNum) && sharesNum > 0 && sharesNum > heldNum;

  const handleRedeem = useCallback(async () => {
    if (!connected || !shares) return;
    await writes.requestRedeem(shares, connected, connected);
    setShares("");
    await refetchAll();
  }, [connected, shares, writes, refetchAll]);

  const setMax = useCallback(() => {
    if (!userPosition) return;
    setShares(userPosition.shareBalance);
  }, [userPosition]);

  const canRedeem =
    isOnCorrectChain &&
    !!connected &&
    !!shares &&
    sharesNum > 0 &&
    !exceedsHeld &&
    !writes.isPending;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-thema-micro">
          Request redeem
        </span>
      </div>

      <p className="text-[12px] text-thema-text-soft/70 leading-relaxed">
        Shares are escrowed and queued for the next settlement epoch. The
        settler commits a share price, finalizes the epoch, and your stablecoins
        become claimable.
      </p>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label className="text-[11px] text-thema-micro font-mono uppercase tracking-[0.15em]">
            Shares to redeem
          </label>
          <span className="text-[11px] text-thema-text-soft/70 font-mono">
            Held:{" "}
            {userPosition === null
              ? "…"
              : `${formatAmount(userPosition.shareBalance, 6)}`}
          </span>
        </div>
        <div className="relative flex items-center">
          <input
            type="number"
            inputMode="decimal"
            min={0}
            placeholder="0.00"
            value={shares}
            onChange={(e) => setShares(e.target.value)}
            className="w-full bg-white/[0.04] border border-thema-border-hairline rounded-md px-4 py-3 text-sm text-white/90 placeholder:text-white/25 outline-none focus:border-thema-royal-blue/60 transition-colors pr-24"
          />
          <div className="absolute right-2 flex items-center gap-2">
            <button
              type="button"
              onClick={setMax}
              disabled={heldNum <= 0}
              className="font-mono text-[10px] uppercase tracking-[0.15em] text-thema-royal-blue hover:underline disabled:opacity-40 disabled:hover:no-underline"
            >
              Max
            </button>
            {/* <span className="text-[11px] text-white/40 font-mono">
              {shareSymbol}
            </span> */}
          </div>
        </div>

        {exceedsHeld && (
          <p className="text-[11px] text-red-300/90">
            Amount exceeds shares held (
            {formatAmount(userPosition?.shareBalance ?? "0", 6)} {shareSymbol}).
          </p>
        )}

        {vaultState && (
          <p className="text-[11px] text-thema-text-soft/60">
            Share price ≈ {formatAmount(vaultState.sharePrice, 6)} {assetSymbol}
            {" "}per share (current — settlement price is fixed at epoch close)
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={handleRedeem}
        disabled={!canRedeem}
        className="w-full px-4 py-3 rounded-md border border-amber-500/50 bg-amber-500/15 text-white text-sm font-medium transition hover:bg-amber-500/25 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-amber-500/15"
      >
        {writes.isPending ? "Submitting…" : "Request redeem"}
      </button>

      <p className="text-[11px] text-thema-text-soft/50">
        Vault: <span className="font-mono">{vault.vaultAddress}</span>
      </p>
    </div>
  );
}

// ─── Claim panel ─────────────────────────────────────────────────────────────

function ClaimPanel({
  assetSymbol,
  isOnCorrectChain,
  connected,
  userPosition,
  writes,
  refetchAll,
}: TabsProps) {
  const [requestId, setRequestId] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);

  const claimableNum = Number(userPosition?.claimableAssets ?? "0");
  const hasClaimable = claimableNum > 0;

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refetchAll();
    } finally {
      setIsRefreshing(false);
    }
  }, [refetchAll]);

  const handleClaim = useCallback(async () => {
    if (!connected || !requestId) return;
    await writes.claimRedeem(requestId, connected);
    setRequestId("");
    await refetchAll();
  }, [connected, requestId, writes, refetchAll]);

  // Claim is only valid once the request's epoch is finalized — that's when
  // `claimableAssets` flips above zero. Without this gate the user could
  // submit a tx for a still-pending request and pay gas just to revert with
  // EpochNotFinalized / NotSettled.
  const canClaim =
    isOnCorrectChain &&
    !!connected &&
    !!requestId &&
    Number(requestId) > 0 &&
    hasClaimable &&
    !writes.isPending;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-thema-micro">
          Claim
        </span>
      </div>

      <p className="text-[12px] text-thema-text-soft/70 leading-relaxed">
        Once your epoch is finalized, enter the request ID to withdraw
        cbBtc. Funds remain reserved until claimed.
      </p>

      <div className="rounded-sm border border-thema-border-hairline bg-white/[0.015] p-4 space-y-1">
        <div className="flex items-center justify-between gap-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-thema-micro">
            Currently claimable
          </p>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isRefreshing}
            title="Refresh on-chain state"
            className="font-mono text-[10px] uppercase tracking-[0.15em] text-thema-royal-blue hover:underline disabled:opacity-50 disabled:hover:no-underline inline-flex items-center gap-1.5"
          >
            <svg
              className={`h-3 w-3 ${isRefreshing ? "animate-spin" : ""}`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 12a9 9 0 1 1-3.5-7.1" />
              <path d="M21 3v6h-6" />
            </svg>
            {isRefreshing ? "Refreshing" : "Refresh"}
          </button>
        </div>
        <p className="text-[16px] font-medium tabular-nums text-thema-teal/90">
          {userPosition === null
            ? "…"
            : `${formatAmount(userPosition.claimableAssets, 8)} ${assetSymbol}`}
        </p>
        {!hasClaimable && userPosition && (
          <p className="text-[11px] text-thema-text-soft/60">
            Nothing finalized yet. Pending shares:{" "}
            {formatAmount(userPosition.pendingShares, 6)}. Auto-refreshes every
            15s.
          </p>
        )}
      </div>

      {userPosition && userPosition.requestIds.length > 0 && (
        <div className="space-y-1.5">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-thema-micro">
            Your request IDs
          </p>
          <div className="flex flex-wrap gap-2">
            {userPosition.requestIds.map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => setRequestId(String(id))}
                className={`px-3 py-1.5 rounded-sm border text-[12px] font-mono transition-colors ${
                  requestId === String(id)
                    ? "border-thema-teal/60 bg-thema-teal/10 text-thema-teal"
                    : "border-thema-border-hairline bg-white/[0.02] text-white/60 hover:text-white/80 hover:bg-white/[0.04]"
                }`}
              >
                #{id}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        <label className="text-[11px] text-thema-micro font-mono uppercase tracking-[0.15em]">
          Request ID
        </label>
        <input
          type="number"
          inputMode="numeric"
          min={1}
          placeholder="1"
          value={requestId}
          onChange={(e) => setRequestId(e.target.value)}
          className="w-full bg-white/[0.04] border border-thema-border-hairline rounded-md px-4 py-3 text-sm text-white/90 placeholder:text-white/25 outline-none focus:border-thema-royal-blue/60 transition-colors"
        />
      </div>

      <button
        type="button"
        onClick={handleClaim}
        disabled={!canClaim}
        className="w-full px-4 py-3 rounded-md border border-thema-teal/50 bg-thema-teal/15 text-white text-sm font-medium transition hover:bg-thema-teal/25 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-thema-teal/15"
      >
        {writes.isPending ? "Claiming…" : "Claim stablecoins"}
      </button>
    </div>
  );
}
